import { useEffect, useRef } from "react";
import * as THREE from "three";
import { motion } from "framer-motion";

const NODE_TYPES = [
  "Ideas",
  "Entrepreneurs",
  "Startups",
  "Businesses",
  "Technology",
  "Investors",
  "Customers",
  "Employees",
  "Partners",
  "Communities",
];

const COUNT_STEPS = ["1 IDEA", "10", "100", "1,000", "10,000", "1,00,000"];

export function EcosystemNetwork() {
  const mountRef = useRef(null);
  const countRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      return;
    }
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.innerWidth < 768;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.set(0, 1.4, 9.5);

    const group = new THREE.Group();
    scene.add(group);

    const core = new THREE.Mesh(
      new THREE.SphereGeometry(0.3, 24, 24),
      new THREE.MeshBasicMaterial({ color: 0xff8800 })
    );
    group.add(core);
    const coreGlow = new THREE.Mesh(
      new THREE.SphereGeometry(0.62, 24, 24),
      new THREE.MeshBasicMaterial({
        color: 0xff8800,
        transparent: true,
        opacity: 0.16,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    group.add(coreGlow);

    const N = isMobile ? 700 : 1500;
    const palette = [new THREE.Color("#FF8800"), new THREE.Color("#00B0FF"), new THREE.Color("#00E676")];
    const pos = new Float32Array(N * 3);
    const col = new Float32Array(N * 3);
    const nodes = [];
    for (let i = 0; i < N; i++) {
      const r = Math.pow(Math.random(), 0.55) * 5.4;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const x = r * Math.sin(phi) * Math.cos(theta);
      const y = r * Math.cos(phi) * 0.72;
      const z = r * Math.sin(phi) * Math.sin(theta);
      nodes.push([x, y, z]);
      pos[i * 3] = x;
      pos[i * 3 + 1] = y;
      pos[i * 3 + 2] = z;
      const c = palette[i % 3];
      col[i * 3] = c.r;
      col[i * 3 + 1] = c.g;
      col[i * 3 + 2] = c.b;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
    const pointsMat = new THREE.PointsMaterial({
      size: 0.05,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const points = new THREE.Points(geo, pointsMat);
    group.add(points);

    // Growth lines: each node links to a nearby earlier node (or the core)
    const linePos = new Float32Array((N - 1) * 6);
    for (let i = 1; i < N; i++) {
      let best = -1;
      let bestD = Infinity;
      const candidates = Math.min(i, 36);
      for (let k = 0; k < candidates; k++) {
        const j = Math.floor(Math.random() * i);
        const dx = nodes[i][0] - nodes[j][0];
        const dy = nodes[i][1] - nodes[j][1];
        const dz = nodes[i][2] - nodes[j][2];
        const d = dx * dx + dy * dy + dz * dz;
        if (d < bestD) {
          bestD = d;
          best = j;
        }
      }
      const o = (i - 1) * 6;
      linePos[o] = nodes[i][0];
      linePos[o + 1] = nodes[i][1];
      linePos[o + 2] = nodes[i][2];
      linePos[o + 3] = best === 0 ? 0 : nodes[best][0];
      linePos[o + 4] = best === 0 ? 0 : nodes[best][1];
      linePos[o + 5] = best === 0 ? 0 : nodes[best][2];
    }
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute("position", new THREE.BufferAttribute(linePos, 3));
    const lineMat = new THREE.LineBasicMaterial({
      color: 0x3b82a8,
      transparent: true,
      opacity: 0.28,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const lines = new THREE.LineSegments(lineGeo, lineMat);
    group.add(lines);

    const onResize = () => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("resize", onResize);

    let visible = true;
    const io = new IntersectionObserver(([e]) => (visible = e.isIntersecting), { threshold: 0 });
    io.observe(mount);

    let raf;
    let shown = 1;
    let holdFrames = 0;
    let t = 0;
    const perFrame = Math.max(2, Math.floor(N / 340));
    const animate = () => {
      raf = requestAnimationFrame(animate);
      if (!visible) return;
      t += 0.004;

      if (!reduced) {
        if (shown < N) {
          shown = Math.min(N, shown + perFrame);
        } else {
          holdFrames += 1;
          if (holdFrames > 240) {
            shown = 1;
            holdFrames = 0;
          }
        }
      } else {
        shown = N;
      }

      geo.setDrawRange(0, shown);
      lineGeo.setDrawRange(0, Math.max(0, (shown - 1) * 2));

      if (countRef.current) {
        const idx = Math.min(COUNT_STEPS.length - 1, Math.floor((shown / N) * COUNT_STEPS.length));
        const label = COUNT_STEPS[idx];
        if (countRef.current.textContent !== label) countRef.current.textContent = label;
      }

      group.rotation.y = t;
      coreGlow.material.opacity = 0.13 + Math.sin(t * 5) * 0.06;
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("resize", onResize);
      [geo, lineGeo, core.geometry, coreGlow.geometry].forEach((g) => g.dispose());
      [pointsMat, lineMat, core.material, coreGlow.material].forEach((m) => m.dispose());
      mount.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []);

  return (
    <section data-testid="ecosystem-network-section" className="relative py-20 sm:py-28 lg:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-6"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">The LaunchLab Ecosystem</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="ecosystem-network-headline">
            ONE POINT OF LIGHT. <span className="text-gradient-saffron">A NETWORK OF IMPACT.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">
            At the center: LaunchLab. Around it, thousands of glowing nodes — and the network
            never stops expanding.
          </p>
        </motion.div>

        <div className="cinema relative h-[420px] sm:h-[540px] rounded-2xl overflow-hidden" data-testid="ecosystem-3d-network">
          <div ref={mountRef} className="absolute inset-0" />
          <div className="absolute top-5 left-1/2 -translate-x-1/2 text-center pointer-events-none">
            <p
              ref={countRef}
              className="font-display font-extrabold text-4xl sm:text-6xl text-gradient-saffron tabular-nums"
              data-testid="ecosystem-counter"
            >
              1 IDEA
            </p>
            <p className="font-mono-tech text-[10px] sm:text-xs tracking-[0.35em] text-slate-400 uppercase mt-1">
              and multiplying
            </p>
          </div>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-wrap justify-center gap-2 max-w-2xl px-4" data-testid="ecosystem-node-legend">
            {NODE_TYPES.map((n) => (
              <span key={n} className="glass px-3 py-1 rounded-full font-mono-tech text-[9px] tracking-[0.18em] text-slate-300 uppercase">
                {n}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
